/**
 * Apply favicon from General Settings (system-setting) to the document head.
 * Replaces the static favicon.ico from public/index.html when a URL is configured.
 */

const LINK_ID = "app-dynamic-favicon";

function isSafeFaviconHref(href: string): boolean {
  const t = href.trim();
  if (!t) {
    return false;
  }
  const lower = t.toLowerCase();
  if (
    lower.startsWith("javascript:") ||
    lower.startsWith("data:") ||
    lower.startsWith("vbscript:")
  ) {
    return false;
  }
  return true;
}

function withCacheBust(href: string): string {
  try {
    const u = href.startsWith("http")
      ? new URL(href)
      : new URL(href, window.location.origin);
    u.searchParams.set("_fv", String(Date.now()));
    return u.toString();
  } catch {
    return href;
  }
}

export function setDocumentFavicon(href: string | null | undefined): void {
  if (!href || typeof href !== "string") {
    return;
  }
  if (!isSafeFaviconHref(href)) {
    return;
  }

  let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (link) {
      link.id = LINK_ID;
    }
  }
  if (!link) {
    link = document.createElement("link");
    link.id = LINK_ID;
    link.rel = "icon";
    document.head.appendChild(link);
  }

  link.type = "";
  const lower = href.trim().toLowerCase();
  if (lower.endsWith(".svg")) {
    link.type = "image/svg+xml";
  } else if (lower.endsWith(".png")) {
    link.type = "image/png";
  } else if (lower.endsWith(".ico")) {
    link.type = "image/x-icon";
  }

  link.href = withCacheBust(href.trim());
}

export function pickFaviconFromApiPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const p = payload as Record<string, unknown>;
  const direct = p.FullImagePathFavicon;
  if (typeof direct === "string" && direct) {
    return direct;
  }
  const nested = p.data;
  if (nested && typeof nested === "object") {
    const d = (nested as Record<string, unknown>).FullImagePathFavicon;
    if (typeof d === "string" && d) {
      return d;
    }
  }
  return null;
}

/** Fetch singleton general settings and apply favicon (requires auth / axios defaults). */
export async function applyFaviconFromGeneralSettings(): Promise<void> {
  try {
    const { default: GeneralSettings } = await import(
      "@/API/GeneralSettings/GeneralSettings"
    );
    const api = new GeneralSettings();
    const rows = (await api.getAll({
      filter: `type|generalSettings|=`,
    })) as Array<{ FullImagePathFavicon?: string }>;
    const href = rows?.[0]?.FullImagePathFavicon;
    setDocumentFavicon(href);
  } catch {
    // Not logged in or API unavailable — keep default favicon.ico
  }
}
